import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import Logo from "@/components/Logo";
import AdminContactsSection from "@/components/AdminContactsSection";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtNum } from "@/utils/toEnglishDigits";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import {
  Crown,
  Target,
  Diamond,
  Sparkle,
  MapPin,
  Star,
  ShieldCheck,
  Clock,
  Car as CarIcon,
  Path,
  Phone,
  WhatsappLogo,
  CheckCircle,
  Mosque,
  ArrowsInLineHorizontal,
  Mountains,
  Waves,
  UserCircleCheck,
  ArrowRight,
  SignIn,
  Buildings,
  UserPlus,
} from "@phosphor-icons/react";

const CITIES = [
  { name: "مكة المكرمة", icon: Mosque, subtitle: "الحرم المكي الشريف" },
  { name: "المدينة المنورة", icon: Mosque, subtitle: "الحرم النبوي الشريف" },
  { name: "الطائف", icon: Mountains, subtitle: "مدينة الجبال والورود" },
  { name: "جدة", icon: Waves, subtitle: "عروس البحر الأحمر" },
];

const ZIYARAT = [
  { name: "مزارات مكة المكرمة", desc: "غار حراء، غار ثور، عرفات، مِنى" },
  { name: "مزارات المدينة المنورة", desc: "قباء، الشهداء، البقيع، الجبل" },
  { name: "جولة سياحية في المدينة", desc: "المعالم التاريخية والدينية" },
];

const VALUES = [
  { icon: ShieldCheck, title: "الجودة", desc: "معايير عالمية في كل تفاصيل الخدمة" },
  { icon: ShieldCheck, title: "الأمان", desc: "سائقون معتمدون وسيارات محدّثة الصيانة" },
  { icon: Clock, title: "الالتزام", desc: "الوقت في الحساب — نصل قبل موعدك دائماً" },
  { icon: Diamond, title: "الفخامة", desc: "أسطول من السيارات الفاخرة والراقية" },
  { icon: Crown, title: "الاحترام", desc: "خدمة تليق بمكانة ضيوف الرحمن" },
];

const SERVICES = [
  "نقل من وإلى المطارات",
  "التنقلات داخل المدن الأربع (مكة، المدينة، الطائف، جدة)",
  "زيارة الأماكن المقدسة والمزارات",
  "الجولات السياحية الدينية",
  "خدمات كبار الشخصيات (VIP)",
  "حجوزات الوفود والفعاليات",
  "خدمات نقل الشركات",
];

const CARS = [
  { name_ar: "لكزس", name_en: "Lexus", img: "https://images.unsplash.com/photo-1568074532071-765285f24854?w=800&auto=format&fit=crop" },
  { name_ar: "جي إم سي", name_en: "GMC", img: "https://images.unsplash.com/photo-1775318289640-69007323374a?w=800&auto=format&fit=crop" },
  { name_ar: "فورد", name_en: "Ford", img: "https://images.unsplash.com/photo-1704476944918-c1258561ebb9?w=800&auto=format&fit=crop" },
  { name_ar: "هيونداي ستاريا", name_en: "Hyundai Staria", img: "https://images.unsplash.com/photo-1600661653561-629509216228?w=800&auto=format&fit=crop" },
  { name_ar: "تويوتا هايس", name_en: "Toyota Hiace", img: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800&auto=format&fit=crop" },
  { name_ar: "تويوتا كوستر", name_en: "Toyota Coaster", img: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=800&auto=format&fit=crop" },
  { name_ar: "الحافلة الكبيرة", name_en: "Bus", img: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=800&auto=format&fit=crop" },
];

const DRIVERS_TRAITS = [
  "خبرة في مسارات مكة والمدينة والطائف وجدة",
  "ملتزمون بأخلاقيات المهنة وقواعد السلامة",
  "رخصة قيادة سارية وسجل قيادة نظيف",
  "تدريب مستمر على معايير الخدمة الفاخرة",
];

const STEPS = [
  { n: "1", title: "احجز", desc: "عبر الموقع، التطبيق، أو الاتصال المباشر" },
  { n: "2", title: "أكّد", desc: "نرسل تأكيد الحجز مع بيانات السائق ورقم اللوحة" },
  { n: "3", title: "استمتع", desc: "سائقك جاهز في الموعد المحدد بلا تأخير" },
  { n: "4", title: "قيّم", desc: "شاركنا رأيك — نسعى للتحسين المستمر" },
];

const CONTACT_TEXT = "السلام عليكم، أرغب بالاستفسار عن خدمات مراسم الزوار.";

const SectionTitle = ({ icon: Icon, children, id }) => (
  <div id={id} className="flex items-center gap-3 mb-8" data-testid={id ? `section-${id}` : undefined}>
    {Icon && (
      <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
        <Icon size={22} weight="duotone" className="text-primary" />
      </div>
    )}
    <h2 className="text-2xl md:text-3xl font-bold text-primary">{children}</h2>
  </div>
);

export default function About() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAuthed = !!user;
  const [mgmt, setMgmt] = useState({ gm: null, booking_mgr: null });
  useEffect(() => {
    document.title = "مراسم الزوار | النقل الفاخر في مكة المدينة الطائف جدة";
    const upsert = (attr, key, val, content) => {
      let el = document.querySelector(`meta[${key}="${val}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(key, val);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    upsert("meta", "name", "description",
      "مراسم الزوار — خدمات النقل الفاخر في مكة المكرمة والمدينة المنورة والطائف وجدة. نقل VIP للحجاج والمعتمرين والزوار مع أسطول حديث.");
    upsert("meta", "name", "keywords",
      "نقل مكة, نقل المدينة, نقل الطائف, نقل جدة, تاكسي مكة, ليموزين مكة, نقل حجاج, نقل معتمرين, مراسم الزوار, مزارات");
    // Fetch admin contacts only when authenticated (endpoint requires auth).
    if (isAuthed) {
      api.get("/company-management").then(({ data }) => setMgmt(data || {})).catch(() => {});
    }
    return () => { document.title = "مراسم الزوار | خدمات النقل الفاخر"; };
  }, [isAuthed]);

  const bmPhoneDigits = String(mgmt?.booking_mgr?.phone || "").replace(/\D/g, "");
  const waHref = bmPhoneDigits
    ? `https://wa.me/${bmPhoneDigits}?text=${encodeURIComponent(CONTACT_TEXT)}`
    : null;
  const prettify = (raw) => formatPhoneDisplay(raw) || "";

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl" data-testid="about-page">
      {isAuthed ? (
        <TopBar />
      ) : (
        <header
          className="sticky top-0 z-40 backdrop-blur-md border-b border-primary/20"
          style={{ background: "rgba(5, 10, 21, 0.85)" }}
        >
          <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
            <button
              type="button"
              onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/login"))}
              data-testid="about-back"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/40 text-primary hover:bg-primary/20 text-sm font-bold transition"
            >
              <ArrowRight size={14} weight="bold" /> رجوع
            </button>
            <div className="text-primary font-bold text-base">عن الشركة</div>
            <div className="w-[70px]" aria-hidden />
          </div>
        </header>
      )}

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #050A15 0%, #0B1A3A 100%)" }}
      >
        {/* Decorative rings */}
        <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-40 right-0 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,169,97,0.08) 0%, transparent 70%)", filter: "blur(60px)" }} />
          <div className="absolute -bottom-40 left-0 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(201,169,97,0.06) 0%, transparent 70%)", filter: "blur(80px)" }} />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 py-16 md:py-24 text-center">
          <div className="flex justify-center mb-6">
            <Logo variant="large" size={240} className="logo-hero" />
          </div>
          <div className="text-primary/80 uppercase tracking-[0.35em] text-xs md:text-sm mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            EST · MARASIM ALZUWAR
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
            <span className="text-primary">مراسم الزوار</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg mt-4 leading-relaxed">
            خدمات النقل الفاخر لضيوف الرحمن في مكة المكرمة والمدينة المنورة والطائف وجدة
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            {isAuthed ? (
              <Link
                to="/new-booking"
                data-testid="hero-cta-book"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-full font-bold transition-all"
              >
                <CarIcon size={18} weight="fill" /> حجز جديد
              </Link>
            ) : (
              <>
                <Link
                  to="/register"
                  data-testid="hero-cta-register-company"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-full font-bold transition-all"
                >
                  <Buildings size={18} weight="fill" /> سجّل شركتك الآن
                </Link>
                <Link
                  to="/driver-register"
                  data-testid="hero-cta-register-driver"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-primary/40 text-primary hover:bg-primary/10 rounded-full font-bold transition-all"
                >
                  <UserPlus size={18} weight="fill" /> سجّل كسائق
                </Link>
              </>
            )}
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-cta-wa"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#22c15c] text-white rounded-full font-bold transition-all"
              >
                <WhatsappLogo size={18} weight="fill" /> واتساب
              </a>
            )}
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <SectionTitle icon={Crown} id="who">من نحن؟</SectionTitle>
        <div className="bg-card border border-primary/15 rounded-2xl p-6 md:p-8 leading-loose text-white/85 text-base md:text-lg">
          <p className="text-primary text-lg md:text-xl font-bold mb-4">
            الاسم الأول في خدمات النقل الفاخر لضيوف الرحمن.
          </p>
          <p className="mb-4">
            نحن شركة سعودية متخصصة في توفير أرقى وسائل النقل والمواصلات
            للحجاج والمعتمرين والزوار الكرام في مكة المكرمة والمدينة المنورة والطائف وجدة،
            مع الالتزام بأعلى معايير الجودة والراحة والأمان.
          </p>
          <p className="text-white/60">
            نؤمن أن رحلة الضيف تبدأ من لحظة نزوله المطار — لذلك نحرص على تقديم تجربة تنقّل هادئة، مريحة، وفاخرة، تليق بقدسية الرحلة وأهميتها في قلوب الزوار.
          </p>
        </div>

        {/* Bilingual SEO block — helps Google index brand name variants for both languages */}
        <div
          className="mt-5 bg-card/60 border border-primary/10 rounded-2xl p-6 md:p-8 text-white/75 leading-relaxed"
          dir="ltr"
          lang="en"
          data-testid="about-en-seo"
        >
          <h2 className="text-primary font-bold text-xl md:text-2xl mb-3">
            About Marasim Alzuwar (Mrasm Alzwar)
          </h2>
          <p className="text-sm md:text-base">
            <strong className="text-primary">Marasim Alzuwar</strong> — also written as{" "}
            <em>Mrasm Alzwar</em>, <em>Marasim Al Zuwar</em>, <em>Marasim Alzwar</em>,{" "}
            <em>Mrasmalzwar</em>, or <em>مراسم الزوار</em> in Arabic — is Saudi Arabia's
            leading platform for premium VIP transportation services. We provide luxury
            chauffeur services with a fleet of high-end vehicles (Mercedes, Lexus, GMC,
            Toyota Hiace, Toyota Coaster, and buses) across Makkah, Madinah, Taif, Jeddah,
            and all major cities in the Kingdom of Saudi Arabia. Our services are available
            24/7 with professional trained drivers to ensure the highest quality of service
            for VIP guests, pilgrims, and Ziyarat visitors.
          </p>
          <p className="text-white/55 text-xs md:text-sm mt-3">
            Book your ride with Marasim Alzuwar for airport transfers, city-to-city trips,
            holy sites visits (Ziyarat), and executive corporate transportation.
          </p>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-card border border-primary/15 rounded-2xl p-6" data-testid="vision-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Target size={20} weight="duotone" className="text-primary" />
            </div>
            <h3 className="text-xl font-bold text-primary">رؤيتنا</h3>
          </div>
          <p className="text-white/80 leading-loose">
            أن نكون الخيار الأول لضيوف بيت الله الحرام في خدمات النقل الفاخرة، من خلال تقديم تجربة سفر مميزة تليق بمكانتهم وقدسية رحلتهم.
          </p>
        </div>
        <div className="bg-card border border-primary/15 rounded-2xl p-6" data-testid="mission-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Diamond size={20} weight="duotone" className="text-primary" />
            </div>
            <h3 className="text-xl font-bold text-primary">رسالتنا</h3>
          </div>
          <p className="text-white/80 leading-loose">
            توفير خدمات نقل فاخرة وموثوقة تجمع بين الرفاهية والانضباط، مع أسطول حديث وسائقين محترفين، لضمان راحة ضيوفنا في كل رحلة.
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <SectionTitle icon={Sparkle} id="values">قيمنا</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {VALUES.map((v, i) => (
            <div
              key={v.title}
              data-testid={`value-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-2xl p-5 text-center hover:border-primary/40 transition-all"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mb-3">
                <v.icon size={22} weight="duotone" className="text-primary" />
              </div>
              <div className="text-primary font-bold text-lg">{v.title}</div>
              <div className="text-white/60 text-xs mt-2 leading-relaxed">{v.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <SectionTitle icon={MapPin} id="locations">المواقع التي نخدمها</SectionTitle>

        <h3 className="text-white/70 text-sm uppercase tracking-widest mb-4">المدن الرئيسية</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {CITIES.map((c, i) => (
            <div
              key={c.name}
              data-testid={`city-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-2xl p-5 hover:border-primary/50 transition-all group"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center group-hover:bg-primary/20 transition-all">
                <c.icon size={22} weight="duotone" className="text-primary" />
              </div>
              <div className="text-primary font-bold text-lg mt-3">{c.name}</div>
              <div className="text-white/50 text-xs mt-1">{c.subtitle}</div>
            </div>
          ))}
        </div>

        <h3 className="text-white/70 text-sm uppercase tracking-widest mb-4">الزيارات والمزارات</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ZIYARAT.map((z, i) => (
            <div
              key={z.name}
              data-testid={`ziyara-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-2xl p-5"
              style={{ animationDelay: `${(i + 4) * 60}ms` }}
            >
              <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                <Star size={20} weight="duotone" className="text-primary" />
              </div>
              <div className="text-primary font-bold mt-3">{z.name}</div>
              <div className="text-white/60 text-sm mt-1">{z.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <SectionTitle icon={CarIcon} id="services">خدماتنا</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {SERVICES.map((s, i) => (
            <div
              key={s}
              data-testid={`service-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-xl p-4 flex items-start gap-3 hover:border-primary/40 transition-all"
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <CheckCircle size={20} weight="fill" className="text-primary flex-shrink-0 mt-0.5" />
              <span className="text-white/85 text-sm md:text-base leading-relaxed">{s}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FLEET */}
      <section className="max-w-6xl mx-auto px-4 py-14">
        <SectionTitle icon={CarIcon} id="fleet">أسطولنا</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {CARS.map((c, i) => (
            <div
              key={c.name_en}
              data-testid={`car-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-2xl overflow-hidden hover:border-primary/50 transition-all"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="aspect-video overflow-hidden bg-[#0B1A3A] relative">
                <img
                  src={c.img}
                  alt={c.name_ar}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  style={{ opacity: 0.85 }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <div className="p-4">
                <div className="text-primary font-bold">{c.name_ar}</div>
                <div className="text-white/40 text-[10px] tracking-[0.2em] uppercase mt-1">{c.name_en}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* DRIVERS */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <SectionTitle icon={UserCircleCheck} id="drivers">سائقونا</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DRIVERS_TRAITS.map((t, i) => (
            <div
              key={t}
              data-testid={`driver-trait-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-xl p-4 flex items-start gap-3"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <ShieldCheck size={20} weight="fill" className="text-primary flex-shrink-0 mt-0.5" />
              <span className="text-white/85 text-sm md:text-base">{t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="max-w-5xl mx-auto px-4 py-14">
        <SectionTitle icon={Path} id="how">كيف نعمل؟</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`step-${i}`}
              className="fadeup bg-card border border-primary/15 rounded-2xl p-5 relative"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              <div className="w-11 h-11 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center text-lg num">
                {fmtNum(s.n)}
              </div>
              <div className="text-primary font-bold text-lg mt-4">{s.title}</div>
              <div className="text-white/60 text-sm mt-2 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* MANAGEMENT — shared AdminContactsSection (GM: call-only, Booking Mgr: call + WA) */}
      <section className="max-w-5xl mx-auto px-4 py-14" id="contact">
        <SectionTitle icon={UserCircleCheck} id="contact-title">الإدارة</SectionTitle>
        <p className="text-white/60 text-sm mb-6 -mt-4">تواصل مباشرة مع فريق الإدارة</p>
        <AdminContactsSection title={null} testId="about-contacts" />
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div
          className="relative rounded-3xl border border-primary/30 overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0F1F42 0%, #0B1A3A 50%, #050A15 100%)",
          }}
        >
          <div className="absolute inset-0 opacity-30 pointer-events-none" aria-hidden="true">
            <div
              className="absolute inset-0"
              style={{
                background: "radial-gradient(circle at 50% 0%, rgba(201,169,97,0.12) 0%, transparent 60%)",
                filter: "blur(30px)",
              }}
            />
          </div>
          <div className="relative p-8 md:p-12">
            <ArrowsInLineHorizontal size={36} weight="duotone" className="text-primary mx-auto mb-4" />
            <h2 className="text-3xl md:text-4xl font-bold text-white">جاهز لبدء رحلتك؟</h2>
            <p className="text-white/60 mt-3 max-w-lg mx-auto">
              انضم إلى مئات الشركات التي تثق بمراسم الزوار في تنقلات ضيوفها
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/new-booking"
                data-testid="cta-book"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-full font-bold transition-all"
              >
                <CarIcon size={18} weight="fill" /> ابدأ حجزاً جديداً
              </Link>
              {waHref && (
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cta-wa"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#25D366] hover:bg-[#22c15c] text-white rounded-full font-bold transition-all"
                >
                  <WhatsappLogo size={18} weight="fill" /> تواصل عبر واتساب
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {!isAuthed && (
        <section className="max-w-5xl mx-auto px-4 pb-14" data-testid="public-auth-cta">
          <div className="rounded-2xl border border-primary/25 bg-gradient-to-br from-[#0F2148] to-[#172956] p-6 md:p-8 text-center">
            <h3 className="text-primary text-xl md:text-2xl font-bold mb-2">لديك حساب بالفعل؟</h3>
            <p className="text-white/70 text-sm md:text-base mb-6">سجّل الدخول للوصول لحجوزاتك ولوحة التحكم.</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/login"
                data-testid="public-login-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-full font-bold transition-all"
              >
                <SignIn size={18} weight="bold" /> تسجيل الدخول
              </Link>
              <Link
                to="/driver-register"
                data-testid="public-driver-login-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-transparent border border-primary/50 text-primary hover:bg-primary/10 rounded-full font-bold transition-all"
              >
                <CarIcon size={18} weight="bold" /> دخول السائق
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-primary/15 mt-8">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo variant="brand" size={36} />
          </div>
          <div className="flex items-center gap-6 text-white/50 text-sm">
            <Link to="/about" className="hover:text-primary">عن الشركة</Link>
            {isAuthed && <Link to="/account" className="hover:text-primary">حسابي</Link>}
            <Link to={isAuthed ? "/" : "/login"} className="hover:text-primary">
              {isAuthed ? "الرئيسية" : "تسجيل الدخول"}
            </Link>
          </div>
          <div className="text-white/40 text-xs num">
            © <span className="num">{fmtNum(new Date().getFullYear())}</span> مراسم الزوار
          </div>
        </div>
      </footer>
    </div>
  );
}
